/**
 * Pure tree-mutation helpers for the per-node edit-mode state added in the
 * FK-breadcrumb follow-up. Each TableNode now owns its own `isEditMode` +
 * `pendingChanges`, so the user can drill via the breadcrumb and continue
 * editing the deeper view without losing root-level changes (and vice versa).
 *
 * Split from `tabNavigationTreeHelpers.ts` to keep both files under the
 * 200-line CLAUDE.md cap. Helpers are React-agnostic and pure.
 */

import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
  PendingChange,
} from '../../../core/types/workspaceTypes';

/**
 * Patch a TableNode's edit-mode flag in place. Returns a new tree, or null
 * when the targeted node doesn't exist or isn't a TableNode.
 */
export function setNodeEditMode(
  tree: NavigationTree,
  nodeId: NavigationNodeId,
  isEditMode: boolean,
): NavigationTree | null {
  const node = tree.nodes[nodeId];
  if (!node || node.kind !== 'table') return null;
  return {
    ...tree,
    nodes: {
      ...tree.nodes,
      [node.id]: { ...node, isEditMode } as NavigationNode,
    },
  };
}

/**
 * Patch a TableNode's pending-changes list in place. Returns a new tree, or
 * null when the targeted node doesn't exist or isn't a TableNode.
 */
export function setNodePendingChanges(
  tree: NavigationTree,
  nodeId: NavigationNodeId,
  pendingChanges: ReadonlyArray<PendingChange>,
): NavigationTree | null {
  const node = tree.nodes[nodeId];
  if (!node || node.kind !== 'table') return null;
  return {
    ...tree,
    nodes: {
      ...tree.nodes,
      [node.id]: { ...node, pendingChanges } as NavigationNode,
    },
  };
}

/**
 * True when the node (or any node in its subtree) has at least one pending
 * change. Used by the chip-dismiss confirmation: dismissing a chip whose
 * subtree contains unsaved edits must prompt the user before nuking those
 * edits.
 */
export function subtreeHasPendingChanges(
  tree: NavigationTree,
  nodeId: NavigationNodeId,
): boolean {
  const stack: NavigationNodeId[] = [nodeId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined) break;
    const node = tree.nodes[id];
    if (!node) continue;
    if (node.kind === 'table' && (node.pendingChanges?.length ?? 0) > 0) {
      return true;
    }
    for (const childId of node.childIds) stack.push(childId);
  }
  return false;
}

/**
 * Read the active TableNode's edit-state. Returns null for row-nodes or
 * when there's no tree. Callers fall back to the tab-level state.
 */
export function getActiveTableEditState(
  tree: NavigationTree | null,
): { isEditMode: boolean; pendingChanges: ReadonlyArray<PendingChange> } | null {
  if (!tree) return null;
  const node = tree.nodes[tree.activeNodeId];
  if (!node || node.kind !== 'table') return null;
  return {
    isEditMode: node.isEditMode ?? false,
    pendingChanges: node.pendingChanges ?? [],
  };
}
